import axios from 'axios';

// Create axios instance with base URL
const api = axios.create({
  baseURL: '/api'
});

// Error handler
const handleError = (error) => {
  console.error('API Error:', error);
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    return {
      error: true,
      message: error.response.data.error || 'Server error',
      status: error.response.status
    };
  } else if (error.request) {
    // The request was made but no response was received
    return {
      error: true,
      message: 'No response from server. Please check your connection.',
      status: 0
    };
  } else {
    // Something happened in setting up the request that triggered an Error
    return {
      error: true,
      message: error.message || 'An unexpected error occurred',
      status: 0
    };
  }
};

// API methods
export const fetchPlayers = async () => {
  try {
    const response = await api.get('/players');
    return response.data;
  } catch (error) {
    return handleError(error);
  }
};

export const fetchCourses = async () => {
  try {
    const response = await api.get('/courses');
    return response.data;
  } catch (error) {
    return handleError(error);
  }
};

export const fetchRounds = async () => {
  try {
    const response = await api.get('/rounds');
    return response.data;
  } catch (error) {
    return handleError(error);
  }
};

export const fetchScores = async () => {
  try {
    const response = await api.get('/scores');
    return response.data;
  } catch (error) {
    return handleError(error);
  }
};

export const addRound = async (roundData) => {
  try {
    const response = await api.post('/rounds', roundData);
    return response.data;
  } catch (error) {
    return handleError(error);
  }
};

export const addPlayer = async (playerData) => {
  try {
    const response = await api.post('/players', playerData);
    return response.data;
  } catch (error) {
    return handleError(error);
  }
};

export const addCourse = async (courseData) => {
  try {
    const response = await api.post('/courses', courseData);
    return response.data;
  } catch (error) {
    return handleError(error);
  }
};

export const calculateStandings = async () => {
  try {
    // We'll calculate this client-side using the raw data
    const [scores, players, courses] = await Promise.all([
      fetchScores(),
      fetchPlayers(),
      fetchCourses()
    ]);

    // Check for any errors in the responses
    for (const response of [scores, players, courses]) {
      if (response.error) {
        return response;
      }
    }

    // Group scores by course, then by player
    const scoresByCourse = {};
    scores.forEach(score => {
      const courseKey = score.course_name.toLowerCase();
      if (!scoresByCourse[courseKey]) {
        scoresByCourse[courseKey] = {
          name: score.course_name,
          playerScores: {}
        };
      }
      
      if (!scoresByCourse[courseKey].playerScores[score.player_id]) {
        scoresByCourse[courseKey].playerScores[score.player_id] = {
          playerId: score.player_id,
          playerName: score.player_name,
          scores: []
        };
      }
      
      scoresByCourse[courseKey].playerScores[score.player_id].scores.push({
        score: score.score,
        date: score.date,
        roundId: score.round_id
      });
    });
    
    // Sort scores for each player on each course
    Object.values(scoresByCourse).forEach(course => {
      Object.values(course.playerScores).forEach(playerData => {
        playerData.scores.sort((a, b) => a.score - b.score);
      });
    });
    
    // Calculate points based on top 3 scores for each player on each course
    const standings = [];
    
    players.forEach(player => {
      const playerStanding = {
        id: player.id,
        name: player.name,
        totalPoints: 0,
        coursePoints: {},
        firstPlaces: 0,
        secondPlaces: 0,
        thirdPlaces: 0
      };
      
      // Add points from each course
      Object.values(scoresByCourse).forEach(course => {
        const courseName = course.name;
        playerStanding.coursePoints[courseName] = 0;
        
        // Process each position (1st, 2nd, 3rd best score)
        for (let position = 0; position < 3; position++) {
          // Extract a specific position score (e.g. best, 2nd best, etc.) for all players
          const positionScores = [];
          
          Object.values(course.playerScores).forEach(playerData => {
            if (playerData.scores.length > position) {
              positionScores.push({
                playerId: playerData.playerId,
                playerName: playerData.playerName,
                score: playerData.scores[position].score
              });
            }
          });
          
          // Sort scores for this position
          positionScores.sort((a, b) => a.score - b.score);
          
          // Assign points based on rank
          let rank = 1;
          let lastScore = null;
          let points = 0;
          
          positionScores.forEach((entry, index) => {
            // If score is different from the previous one, update rank
            if (lastScore !== null && entry.score !== lastScore) {
              rank = index + 1;
            }
            
            // Assign points based on rank
            if (rank === 1) points = 3;
            else if (rank === 2) points = 2;
            else if (rank === 3) points = 1;
            else points = 0;
            
            // Update player's points if this is the current player
            if (entry.playerId === player.id) {
              playerStanding.coursePoints[courseName] += points;
              playerStanding.totalPoints += points;
              
              if (points === 3) playerStanding.firstPlaces++;
              else if (points === 2) playerStanding.secondPlaces++;
              else if (points === 1) playerStanding.thirdPlaces++;
            }
            
            lastScore = entry.score;
          });
        }
      });
      
      standings.push(playerStanding);
    });
    
    // Sort by total points (descending)
    standings.sort((a, b) => {
      if (b.totalPoints !== a.totalPoints) {
        return b.totalPoints - a.totalPoints;
      }
      // Tiebreaker: first places
      if (b.firstPlaces !== a.firstPlaces) {
        return b.firstPlaces - a.firstPlaces;
      }
      // Tiebreaker: second places
      if (b.secondPlaces !== a.secondPlaces) {
        return b.secondPlaces - a.secondPlaces;
      }
      // Tiebreaker: third places
      return b.thirdPlaces - a.thirdPlaces;
    });
    
    return standings;
  } catch (error) {
    return handleError(error);
  }
};

export default {
  fetchPlayers,
  fetchCourses,
  fetchRounds,
  fetchScores,
  addRound,
  addPlayer,
  addCourse,
  calculateStandings
}; 